import { c as _c } from "react/compiler-runtime";
import { useId } from 'react';
const PERIOD = 1200;
const WIDTH = PERIOD * 2;
// Deeper than the ribbons, so they have room below to fade out.
const HEIGHT = 800;
// The stroke runs past both edges so the blur is even at the seam; otherwise each wrap would show
// the ribbon's softened ends.
const OVERSCAN = 300;
const LEFT = -OVERSCAN;
const RIGHT = WIDTH + OVERSCAN;
const EDGE_BLUR = 24;
const wavePath = (centre: number, amplitude: number, phase: number) => {
  const steps = 120;
  const points = Array.from({
    length: steps + 1
  }, (_, step) => {
    const x = LEFT + step / steps * (RIGHT - LEFT);
    const y = centre + amplitude * Math.sin(2 * Math.PI * x / PERIOD + phase);
    return `${x.toFixed(1)} ${y.toFixed(1)}`;
  });
  return `M${points.join('L')}`;
};
type RibbonProps = {
  className: string;
  centre: number;
  amplitude: number;
  phase: number;
  thickness: number;
  /** How much of the ribbon's lower part dissolves into the page. */
  fade: number;
};

// Two periods wide, so home-backdrop.css sliding it left by half its width loops without a seam.
const Ribbon = t0 => {
  const $ = _c(47);
  const {
    className,
    centre,
    amplitude,
    phase,
    thickness,
    fade
  } = t0;
  const t1 = useId();
  let t2;
  if ($[0] !== t1) {
    let t3;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = /[^a-zA-Z0-9]/g;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    t2 = t1.replace(t3, "");
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const id = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      x: LEFT,
      y: 0,
      width: RIGHT - LEFT,
      height: HEIGHT
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const canvas = t3;
  const t4 = centre + thickness / 2 - fade / 2;
  let t5;
  if ($[4] !== amplitude || $[5] !== phase || $[6] !== t4) {
    t5 = wavePath(t4, amplitude, phase);
    $[4] = amplitude;
    $[5] = phase;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const fadeLine = t5;
  const t6 = `${id}-fill`;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <stop offset="0" style={{
      stopColor: "var(--percy-aurora-teal)"
    }} />;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <stop offset="0.35" style={{
      stopColor: "var(--percy-aurora-sky)"
    }} />;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <stop offset="0.7" style={{
      stopColor: "var(--percy-aurora-lilac)"
    }} />;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <stop offset="1" style={{
      stopColor: "var(--percy-aurora-teal)"
    }} />;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== t6) {
    t11 = <linearGradient id={t6} gradientUnits="userSpaceOnUse" x1="0" x2={PERIOD} y1="0" y2="0" spreadMethod="repeat">{t7}{t8}{t9}{t10}</linearGradient>;
    $[12] = t6;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  const t12 = `${id}-edge`;
  let t13;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <feGaussianBlur stdDeviation={EDGE_BLUR} />;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  let t14;
  if ($[15] !== t12) {
    t14 = <filter id={t12} filterUnits="userSpaceOnUse" {...canvas}>{t13}</filter>;
    $[15] = t12;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  const t15 = `${id}-fade-blur`;
  const t16 = fade / 4;
  let t17;
  if ($[17] !== t16) {
    t17 = <feGaussianBlur stdDeviation={t16} />;
    $[17] = t16;
    $[18] = t17;
  } else {
    t17 = $[18];
  }
  let t18;
  if ($[19] !== t15 || $[20] !== t17) {
    t18 = <filter id={t15} filterUnits="userSpaceOnUse" {...canvas}>{t17}</filter>;
    $[19] = t15;
    $[20] = t17;
    $[21] = t18;
  } else {
    t18 = $[21];
  }
  const t19 = `${id}-fade`;
  const t20 = `${fadeLine}L${RIGHT} 0L${LEFT} 0Z`;
  const t21 = `url(#${id}-fade-blur)`;
  let t22;
  if ($[22] !== t20 || $[23] !== t21) {
    t22 = <path d={t20} fill="white" filter={t21} />;
    $[22] = t20;
    $[23] = t21;
    $[24] = t22;
  } else {
    t22 = $[24];
  }
  let t23;
  if ($[25] !== t19 || $[26] !== t22) {
    t23 = <mask id={t19} maskUnits="userSpaceOnUse" {...canvas}>{t22}</mask>;
    $[25] = t19;
    $[26] = t22;
    $[27] = t23;
  } else {
    t23 = $[27];
  }
  let t24;
  if ($[28] !== t11 || $[29] !== t14 || $[30] !== t18 || $[31] !== t23) {
    t24 = <defs>{t11}{t14}{t18}{t23}</defs>;
    $[28] = t11;
    $[29] = t14;
    $[30] = t18;
    $[31] = t23;
    $[32] = t24;
  } else {
    t24 = $[32];
  }
  let t25;
  if ($[33] !== amplitude || $[34] !== centre || $[35] !== phase) {
    t25 = wavePath(centre, amplitude, phase);
    $[33] = amplitude;
    $[34] = centre;
    $[35] = phase;
    $[36] = t25;
  } else {
    t25 = $[36];
  }
  const t26 = `url(#${id}-fill)`;
  const t27 = `url(#${id}-edge)`;
  const t28 = `url(#${id}-fade)`;
  let t29;
  if ($[37] !== t25 || $[38] !== t26 || $[39] !== t27 || $[40] !== t28 || $[41] !== thickness) {
    t29 = <path d={t25} fill="none" stroke={t26} strokeWidth={thickness} filter={t27} mask={t28} />;
    $[37] = t25;
    $[38] = t26;
    $[39] = t27;
    $[40] = t28;
    $[41] = thickness;
    $[42] = t29;
  } else {
    t29 = $[42];
  }
  let t30;
  if ($[43] !== className || $[44] !== t24 || $[45] !== t29) {
    t30 = <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} preserveAspectRatio="none" className={className}>{t24}{t29}</svg>;
    $[43] = className;
    $[44] = t24;
    $[45] = t29;
    $[46] = t30;
  } else {
    t30 = $[46];
  }
  return t30;
};
const HomeBackdrop = () => {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="percy-aurora__wash" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div aria-hidden={true} className="percy-aurora">{t0}<div className="percy-aurora__waves"><Ribbon className="percy-aurora__ribbon percy-aurora__ribbon--back" centre={380} amplitude={70} phase={1.4} thickness={370} fade={200} /><Ribbon className="percy-aurora__ribbon percy-aurora__ribbon--front" centre={440} amplitude={45} phase={0} thickness={310} fade={180} /></div><div className="percy-aurora__grain" /></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export default HomeBackdrop;